import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Dialog,
  DialogShell,
  DialogShellBody,
  DialogShellHeader,
  ProcessFlowStepDetailSidebar,
  Skeleton
} from '@bliks-io/ui';
import { useAssigneeLookup } from '@/lib/useAssigneeLookup';
import { readDialogLoadState } from '@/lib/dialogLoadState';
import { DialogLoadStateBody } from '@/components/dialogs/DialogLoadStateBody';
import { useRecordingProcess } from '../hooks/useRecordingProcess';

interface SourceRecordingPreviewDialogProps {
  recordingId: string | null;
  onClose: () => void;
}

export function SourceRecordingPreviewDialog({
  recordingId,
  onClose
}: SourceRecordingPreviewDialogProps) {
  const { t } = useTranslation();
  const { recordingQuery, processQuery, recordingWithoutStep } = useRecordingProcess(
    recordingId ?? ''
  );
  const assigneeLookup = useAssigneeLookup();

  const populatedStep = processQuery.data?.process.processStepRecordings?.find(
    (step) => step._id === recordingId
  );
  const processLoaded = processQuery.data !== undefined;
  const processPredatesStep =
    processLoaded && !populatedStep && processQuery.dataUpdatedAt < recordingQuery.dataUpdatedAt;
  const refetchProcess = processQuery.refetch;

  useEffect(
    function refetchProcessOlderThanRecording() {
      if (processPredatesStep) void refetchProcess();
    },
    [processPredatesStep, refetchProcess]
  );

  if (!recordingId) return null;

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogShell size="sm" className="p-0">
        {populatedStep ? (
          <ProcessFlowStepDetailSidebar
            step={populatedStep}
            onClose={onClose}
            showSensitiveFields={false}
            showVariantCheck={false}
            assigneeLookup={assigneeLookup}
          />
        ) : (
          <>
            <DialogShellHeader title={t('myTasks.processStepGeneric')} onClose={onClose} />
            <DialogShellBody>
              <DialogLoadStateBody
                loadState={readDialogLoadState(
                  [recordingQuery, processQuery],
                  recordingWithoutStep ||
                    (processLoaded && (!processPredatesStep || processQuery.isError))
                )}
                notFoundKey="errors.processStep.notFound"
                skeleton={<SourceStepSkeleton />}
              />
            </DialogShellBody>
          </>
        )}
      </DialogShell>
    </Dialog>
  );
}

function SourceStepSkeleton() {
  return (
    <>
      <Skeleton className="h-6 w-2/3" />
      <Skeleton className="h-4" />
      <Skeleton className="h-4" />
      <Skeleton className="h-4" />
      <Skeleton className="h-24" />
    </>
  );
}
